import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong,  faCircleXmark, faImage, faMask, faPaintBrush, faRotateLeft, faRotateRight} from '@fortawesome/free-solid-svg-icons';
import { fileToBase64, urlToFile } from '../../utils/fileHandling';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { downloadOriginal } from '../../utils/exportMedia';
import { PromptArea } from '../../components/app/generation/PromptArea';
import { FilePlaceHolderWithDragAndDrop } from '../../components/fileupload/filePlaceholderWithDragAndDrop';
import { StatePreview } from '../../components/app/generation/StatePreview';
import { useIsMobile } from '../../hooks/useIsMobile';
import EffectThemeRange from '../../components/effectTheme/effectThemeRange';
import { useNavigateBack } from '../../hooks/useNavigationBack';

export const ImageToImageInPaint = () => {
    const { t } = useTranslation(['generation','toolsApps']);
    const isMobile = useIsMobile();
    const navigateBack = useNavigateBack();
    const [aiGenerationIsRunning, setAiGenerationIsRunning] = useState(false);

    const [imageInput,setImageInput] = useState<{file:any,src:string}|null>(null);
    const [imgMask,setImgMask] = useState<{file:any,src:string}|null>(null);
    const maskDisplayCanvasRef = useRef<HTMLCanvasElement>(null);
    const [maskHistory, setMaskHistory] = useState<string[]>([]);
    const [maskHistoryIndex, setMaskHistoryIndex] = useState(-1);
    const [isDrawing, setIsDrawing] = useState(false);
    const [imageInpaintedStart, setImageInpaintedStart] = useState(false);
    const [maskVisible, setMaskVisible] = useState(true);
    const [editingMask, setEditingMask] = useState(true);

    const [eraserSize, setEraserSize] = useState(30);
    const [hoverPos, setHoverPos] = useState<{x:number, y:number} | null>(null);
    const imageCanvasRef = useRef<HTMLCanvasElement>(null);
    const maskCanvasRef = useRef<HTMLCanvasElement>(null);
    const imagePreviewAreaRef = useRef<HTMLDivElement>(null);
    const [imageCanvasSize, setImageCanvasSize] = useState({
        width: 0,
        height: 0
    });

    const [imageDisplaySize, setImageDisplaySize] = useState({
        width: 0,
        height: 0
    });
    const [inputs,setInputs] = useState<{promptPositive:string,promptNegative:string}>({
        promptPositive:'',
        promptNegative: ''
    });
    const [imgInpainted, setImgInpainted] = useState<{file:any,src:string}|null>(null);
    const [generation,state,setState,getJobID,insufficientCreditsDialog] = useAiImageTypesGeneration();

    const handleGenerationResult = async() => {
        setAiGenerationIsRunning(false);
        let img = generation[1];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setImgInpainted({
            file:imgFile,
            src:img
        });
    };
    const handleImageInPaint = async() => {
        if(!imageInput || !imgMask) return;
        setState(null);
        setAiGenerationIsRunning(true);
        const base64Image = await fileToBase64(imageInput.file);
        const base64Mask = await fileToBase64(imgMask.file);
        const body = { 
            'positive_prompt': inputs.promptPositive, 
            // 'negative_prompt': inputs.imgPromptNegative,
            "image": base64Image,
            "mask": base64Mask
        };
        setState('send');
        let path = 'generate/image-to-image-inpaint'
        try {
            getJobID('image_gen',path,body);
        } catch(err){
            setState('error');
            setAiGenerationIsRunning(false);
        };
    };

    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const uploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file =  event.target.files?.[0] ?? null;
        if (!file) return;
        setInputImageFromFile(file);
    };
    const setInputImageFromFile = (file: File) => {
        if (!file.type.startsWith("image/")) return;

        const imgSrc = URL.createObjectURL(file);

        setImageInput({
            file,
            src: imgSrc
        });
    };
    const setInitialMask = () => {
        //TODO: use full image size and adjust display image with mask and insure correct positions
        if(!imageInput) return;

        const img = new Image();
        img.src = imageInput.src;

        img.onload = () => {
            const imageCanvas = imageCanvasRef.current!;
            const maskCanvas = maskCanvasRef.current!;
            const displayCanvas = maskDisplayCanvasRef.current!;
            const width = img.naturalWidth || img.width;
            const height = img.naturalHeight || img.height;
            setImageCanvasSize({
                width,
                height
            });

            imageCanvas.width = width;
            imageCanvas.height = height;
            maskCanvas.width = width;
            maskCanvas.height = height;
            displayCanvas.width = width;
            displayCanvas.height = height;

            // draw image
            const ctxImg = imageCanvas.getContext("2d")!;
            ctxImg.drawImage(img, 0, 0, width,height);

            // create red mask
            const ctxMask = maskCanvas.getContext("2d")!;
            ctxMask.fillStyle = "rgba(255,0,0,1)";
            ctxMask.fillRect(0,0,width,height);

            //empty displaying mask
            const ctxDisplay = displayCanvas.getContext("2d")!;
            ctxDisplay.clearRect(0, 0, displayCanvas.width, displayCanvas.height);

            // eslint-disable-next-line no-restricted-syntax -- internes Masken-History-Snapshot (kein Download), Art. 50 KI-VO nicht einschlaegig
            const initialSnapshot = maskCanvas.toDataURL("image/png");

            setMaskHistory([initialSnapshot]);
            setMaskHistoryIndex(0);

            updateMaskState();
        };
    };
    
    const download = () => {
        let resource = imgInpainted ? imgInpainted.src : '';
        if(resource === '') return;
        const fileName = resource.replaceAll('\\','/').split('/').slice(-1)[0];
        downloadOriginal(resource, fileName)
            .catch(err => console.error('Download fehlgeschlagen', err));
    };
    // Position Berechnung für Maus + Touch
    const getPos = (canvas: HTMLCanvasElement, evt: any) => {
        const rect = canvas.getBoundingClientRect();
        const clientX = evt.touches?.[0]?.clientX ?? evt.clientX;
        const clientY = evt.touches?.[0]?.clientY ?? evt.clientY;
        const scaleX = canvas.width / rect.width;
        const scaleY =  canvas.height / rect.height;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    // Start Draw: Mouse
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!editingMask) return;
        if (e.buttons === 1) {
            setIsDrawing(true);
            handleDraw(e);
        }
    };

    const finishDrawing = () => {
        if (!isDrawing) return;

        setIsDrawing(false);
        renderMaskDisplay();
        updateMaskState();
        saveMaskSnapshot();
    };
    const handleMouseUp = () => {
        finishDrawing();
    };

    const handleMouseLeave = () => {
        finishDrawing();
        setHoverPos(null);
    };

    // Zeichnen über Maus
    const handleMouseMove = (e: React.MouseEvent) => {
        const canvas = maskCanvasRef.current;
        if (!canvas) return;
        const pos = getPos(canvas, e);
        setHoverPos(pos);

        if (isDrawing && editingMask) {
            handleDraw(e);
        }
    };
    const getMaskDisplayScale = () => {
        const canvas = maskCanvasRef.current;
        if (!canvas) return 1;

        const rect =
            canvas.getBoundingClientRect();

        return rect.width /
            Math.max(1, canvas.width);
    };

    // ----------------------------------------------
    // Touch Events
    // ----------------------------------------------
    const handleTouchStart = (e: React.TouchEvent) => {
        if (!editingMask) return;
        setIsDrawing(true);
        handleDraw(e);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!editingMask || !isDrawing) return;
        e.preventDefault(); // verhindert Scrollen
        handleDraw(e);
    };

    const handleTouchEnd = () => {
        finishDrawing();
    };

    const handleTouchCancel = () => {
        finishDrawing();
    };

    // ----------------------------------------------
    // Zeichenfunktion (Radierer)
    // ----------------------------------------------
    const handleDraw = (evt: any) => {
        const maskCanvas = maskCanvasRef.current!;
        const displayCanvas = maskDisplayCanvasRef.current!;
        const pos = getPos(maskCanvas, evt);

        // Radieren auf Masken-Canvas
        const ctxMask = maskCanvas.getContext("2d")!;
        ctxMask.globalCompositeOperation = "destination-out";
        ctxMask.beginPath();
        ctxMask.arc(pos.x, pos.y, eraserSize, 0, Math.PI * 2);
        ctxMask.fill();

        // Radierte Fläche auf Anzeige-Canvas sichtbar machen
        const ctxDisplay = displayCanvas.getContext("2d")!;
        ctxDisplay.globalCompositeOperation = "source-over";
        ctxDisplay.fillStyle = "rgba(255,140,0,1)";
        ctxDisplay.beginPath();
        ctxDisplay.arc(pos.x, pos.y, eraserSize, 0, Math.PI * 2);
        ctxDisplay.fill();
    };
    const updateMaskState = () => {
        const maskCanvas = maskCanvasRef.current!;
        // eslint-disable-next-line no-restricted-syntax -- internes Masken-State-Update (kein Download), Art. 50 KI-VO nicht einschlaegig
        const dataUrl = maskCanvas.toDataURL("image/png");
        fetch(dataUrl)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], "mask.png", { type: "image/png" });
                setImgMask({ file, src: dataUrl });
            });
    };
    const saveMaskSnapshot = () => {
        const canvas = maskCanvasRef.current;
        if (!canvas) return;

        // eslint-disable-next-line no-restricted-syntax -- internes Masken-History-Snapshot (kein Download), Art. 50 KI-VO nicht einschlaegig
        const snapshot = canvas.toDataURL("image/png");

        setMaskHistory(prev => {
            const next = prev.slice(0, maskHistoryIndex + 1);
            next.push(snapshot);
            setMaskHistoryIndex(next.length - 1);
            return next;
        });
    };
    const restoreMaskSnapshot = (snapshot: string) => {
        const canvas = maskCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const img = new Image();

        img.onload = () => {
            ctx.save();
            ctx.globalCompositeOperation = "source-over";
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.restore();

            renderMaskDisplay();
            updateMaskState();
        };

        img.src = snapshot;
    };
    const undoMask = () => {
        if (maskHistoryIndex <= 0) return;

        const nextIndex = maskHistoryIndex - 1;
        const snapshot = maskHistory[nextIndex];

        setMaskHistoryIndex(nextIndex);
        restoreMaskSnapshot(snapshot);
    };
    const redoMask = () => {
        if (
            maskHistoryIndex < 0 ||
            maskHistoryIndex >= maskHistory.length - 1
        ) {
            return;
        }

        const nextIndex = maskHistoryIndex + 1;
        const snapshot = maskHistory[nextIndex];

        setMaskHistoryIndex(nextIndex);
        restoreMaskSnapshot(snapshot);
    };
    const resetMask = () => {
        if (maskHistory.length === 0) return;

        setMaskHistoryIndex(0);
        restoreMaskSnapshot(maskHistory[0]);
    };
    const renderMaskDisplay = () => {
        if (!maskCanvasRef.current || !maskDisplayCanvasRef.current) return;

        const maskCanvas = maskCanvasRef.current;
        const displayCanvas = maskDisplayCanvasRef.current;

        displayCanvas.width = maskCanvas.width;
        displayCanvas.height = maskCanvas.height;

        const ctxMask = maskCanvas.getContext("2d")!;
        const ctxDisplay = displayCanvas.getContext("2d")!;

        const maskData = ctxMask.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
        const data = maskData.data;

        for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            if (alpha > 0) {
                // noch nicht radiert
                data[i + 3] = 0;
            } else {
                // radiert → orange anzeigen
                data[i] = 255;
                data[i + 1] = 140;
                data[i + 2] = 0;
                data[i + 3] = 255;
            }
        }

        ctxDisplay.putImageData(maskData, 0, 0);
    };
    const maskDisplayScale = getMaskDisplayScale();
    const displayEraserSize = eraserSize * maskDisplayScale;
    
    useEffect(()=>{
            document.title = t('apps:registry.app.imageInpaint.name')
        },[]);
    useEffect(()=>{
        if(generation.length > 0){
            handleGenerationResult();
        }
    },[generation]);
    // Fehlschlag/gesperrtes Modell beendet den Laufzustand - vorher blieb die Seite nach einem Fehler
    // gesperrt (Senden deaktiviert), bis man sie neu lud.
    useEffect(() => {
        if (state === 'error' || state === 'unavailable') setAiGenerationIsRunning(false);
    }, [state]);
    useEffect(() => {
        setImageInpaintedStart(false);
        //create mask
        setInitialMask();
    }, [imageInput]);
    useEffect(() => {
        const container = imagePreviewAreaRef.current;
        if (!container || imageCanvasSize.width <= 0 || imageCanvasSize.height <= 0 ) return;

        const updateDisplaySize = () => {
            const rect = container.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return;
            const scale = Math.min(rect.width / imageCanvasSize.width, rect.height / imageCanvasSize.height);

            setImageDisplaySize({
                width: Math.max(1, Math.floor(imageCanvasSize.width * scale)),
                height: Math.max(1,Math.floor(imageCanvasSize.height * scale))
            });
        };

        const observer = new ResizeObserver(updateDisplaySize);
        observer.observe(container);
        updateDisplaySize();

        return () => {
            observer.disconnect();
        };
    }, [imageCanvasSize.width, imageCanvasSize.height, isMobile]);

  return (
    <>
        {insufficientCreditsDialog}
        <div className="container-fluid h-100 pb-2">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button 
                            onClick={() => navigateBack()} 
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                    <div className='col'/>
                    <div className='col-auto h3 align-items-center'>
                        {t('inpaint.imageToInpaint')}
                    </div>
                    <div className='col'/>
                </div>
                <div className={`row flex-grow-1 p-2 m-0 ${isMobile ? "overflow-y-auto overflow-x-hidden" : ""}`} style={{ minHeight: 0 }}>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className={`col ${isMobile ? 'h6' : 'h4'}`}>
                                {t('step')} 1
                            </div>
                            <div className={`col-auto ${isMobile ? 'h6' : 'h4'}`}>
                                {t('uploadImageAndStartInpaint')}
                            </div>
                            <div className='col'/>
                            { imageInput && (
                                    <div className='col-auto'>
                                        <button 
                                                className="btn border-0 bg-transparent"
                                                onClick={() => {
                                                    if(maskVisible === false){
                                                        setMaskVisible(true);
                                                        setEditingMask(true);
                                                    } else {
                                                        setEditingMask(false);
                                                        setMaskVisible(false);
                                                    }
                                                }}
                                                title={maskVisible ? t('inpaint.hideMask') : t('inpaint.showMask') }
                                            >
                                                <FontAwesomeIcon 
                                                    icon={faMask} 
                                                    size='xl'
                                                />
                                                
                                            </button>
                                    </div>
                                )
                            }
                        </div>
                        {maskVisible && editingMask && imageInput && (
                            <div className='row mt-2 justify-content-center align-items-top'>
                                <div className='col-auto p-0 m-0'>
                                    <label className='text-secondary'>
                                        <FontAwesomeIcon icon={faPaintBrush}/> { !isMobile && <>{t('inpaint.brushSize')}</>}
                                    </label>
                                    <input 
                                        type="number" 
                                        min="5" 
                                        max="100" 
                                        value={eraserSize}
                                        className='border-0 bg-transparent text-secondary'
                                        onChange={e => setEraserSize(parseInt(e.target.value))}
                                    />
                                </div>
                                <div className='col-auto p-0 m-0 me-3' title={t('inpaint.brushSize')}>
                                    <EffectThemeRange
                                        min={5}
                                        max={100}
                                        step={1}
                                        name="brushSize"
                                        value={eraserSize}
                                        onChange={value => setEraserSize(value)}
                                    />
                                </div>
                                <div className='col-auto m-0 p-0 me-1'>
                                    <button 
                                        className="btn border-0 bg-transparent text-secondary"
                                        onClick={undoMask}
                                        title={t('inpaint.undo')}
                                        disabled={maskHistoryIndex <= 0}
                                    >
                                        <FontAwesomeIcon
                                            icon={faRotateLeft}
                                            className='me-2'
                                            size='xl'
                                        />
                                        {!isMobile && (<>{t('inpaint.undo')}</>)}
                                    </button>
                                </div>
                                <div className='col-auto m-0 p-0 me-1'>
                                    <button 
                                        className="btn border-0 bg-transparent text-secondary"
                                        onClick={redoMask}
                                        title={t('inpaint.redo')}
                                        disabled={maskHistoryIndex < 0 || maskHistoryIndex >= maskHistory.length - 1}
                                    >
                                        <FontAwesomeIcon
                                            icon={faRotateRight}
                                            className='me-2'
                                            size='xl'
                                        />
                                        {!isMobile && (<>{t('inpaint.redo')}</>)}
                                    </button>
                                </div>
                                <div className='col-auto m-0 p-0'>
                                    <button 
                                        className="btn border-0 bg-transparent text-secondary"
                                        onClick={resetMask}
                                        title={t('inpaint.reset')}
                                    >
                                        <FontAwesomeIcon
                                            icon={faCircleXmark}
                                            className='me-2'
                                            size='xl'
                                        />
                                        {!isMobile && (<>{t('inpaint.reset')}</>)}
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            ref={imagePreviewAreaRef}
                            style={{ minHeight: isMobile ? imageInput ? "250px": "180px" : 0, maxHeight: isMobile ? "50vh" : "none"}}
                        >
                            {!imageInput ? (
                                <FilePlaceHolderWithDragAndDrop
                                    imageSrc={null}
                                    placeholderIcon={faImage}
                                    placeholderText={t("noUploadedImage")}
                                    onDropImage={setInputImageFromFile}
                                />
                            ) : (
                                <div className='p-0' 
                                    style={{
                                        position: "relative",
                                        width: `${imageDisplaySize.width}px`,
                                        height: `${imageDisplaySize.height}px`,
                                        flex: "0 0 auto"
                                    }}
                                >
                                    {maskVisible && editingMask && hoverPos  && !isDrawing &&  (
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: hoverPos.y * maskDisplayScale - displayEraserSize,
                                                left: hoverPos.x * maskDisplayScale - displayEraserSize,
                                                width: displayEraserSize * 2,
                                                height: displayEraserSize * 2,
                                                borderRadius: "50%",
                                                border: "2px solid #ffffffaa",
                                                pointerEvents: "none",
                                                zIndex: 20
                                            }}
                                        />
                                    )}
                                    {/* Base Image */}
                                    <canvas
                                        ref={imageCanvasRef}
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            height: "100%"
                                        }}
                                    />

                                    <canvas
                                        ref={maskDisplayCanvasRef}
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            pointerEvents: "none",
                                            zIndex: 10,
                                            opacity:
                                                maskVisible
                                                    ? 0.55
                                                    : 0,
                                            filter: "drop-shadow(0 0 1.5px rgba(0,0,0,0.9)) drop-shadow(0 0 1.5px rgba(255,255,255,0.9))"
                                        }}
                                    />
                                    <canvas
                                        ref={maskCanvasRef}
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            opacity: 0,
                                            pointerEvents:
                                                editingMask
                                                    ? "auto"
                                                    : "none",
                                            cursor:
                                                editingMask
                                                    ? "crosshair"
                                                    : "default",
                                            touchAction:
                                                editingMask
                                                    ? "none"
                                                    : "auto"
                                        }}
                                        onMouseDown={handleMouseDown}
                                        onMouseMove={handleMouseMove}
                                        onMouseUp={handleMouseUp}
                                        onMouseLeave={handleMouseLeave}

                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleTouchEnd}
                                        onTouchCancel={handleTouchCancel}
                                    />

                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className={`col ${isMobile ? 'h5' : 'h4'}`}>
                                {t('step')} 2
                            </div>
                            <div className={`col-auto ${isMobile ? 'h5' : 'h4'}`}>
                                {t('inpaint.inpaintImage')}
                            </div>
                            <div className='col'/>
                        </div>
                        <StatePreview state={state} />
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            style={{ minHeight: imgInpainted ? (isMobile ? "250px" : "350px") : "160px", maxHeight: isMobile ? "50vh" : "100%"}}
                        >
                            <FilePlaceHolderWithDragAndDrop 
                                imageSrc={ imgInpainted ? imgInpainted.src : null}
                                imageAltText={t('lastGeneratedImage')}
                                onDownload={() => download()}
                                donwloadTitle={t("download.image")}
                                placeholderIcon={faImage}
                                placeholderText={t('noGeneratedImage')}
                            />
                        </div>
                    </div>
                </div>
                <div className='row mx-2'>
                    <PromptArea
                        functionsDisabled={aiGenerationIsRunning}
                        credits={0}
                        onGenerateImage={handleImageInPaint}
                        promptNegative={inputs.promptNegative ?? ''}
                        promptPositive={inputs.promptPositive ?? ''}
                        placeholderPromptPositive={t('inpaint.placeholderChange')}
                        placeholderPromptNegative={t('inpaint.placeholderKeep')}
                        onInputChange={handleInputChange}
                        onUploadFile={uploadFile}
                    />
                </div>
                {/* Bedienhilfe: Inpaint beschreibt den ZIELZUSTAND des markierten Bereichs. Beispiele fuellen
                    den Prompt per Klick (nur Vorschlag, frei aenderbar). */}
                <div className='row mx-2 mb-2'>
                    <div className='col small text-secondary d-flex flex-wrap align-items-center gap-2'>
                        <span>{t('inpaint.hint')}</span>
                        {(t('inpaint.examples', { returnObjects: true }) as string[]).map(example => (
                            <button
                                key={example}
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill py-0"
                                disabled={aiGenerationIsRunning}
                                onClick={() => setInputs(prev => ({ ...prev, promptPositive: example }))}
                            >
                                {example}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default ImageToImageInPaint;